import { motion } from 'framer-motion';
import { useLang } from '../../../context/LangContext';

const DASHBOARD_IMAGE = "https://customer-assets.emergentagent.com/job_d50654b2-90f4-4249-b46d-f1035aca848d/artifacts/cdq4jq2z_Gemini_Generated_Image_qr9tybqr9tybqr9t.png";
const VOICE_AGENT_IMAGE = "https://customer-assets.emergentagent.com/job_d50654b2-90f4-4249-b46d-f1035aca848d/artifacts/chks1dkw_Gemini_Generated_Image_llgm3hllgm3hllgm.png";

export const AgentHero = () => {
  const { t, lang } = useLang();

  return (
    <section data-testid="agent-hero" className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden bg-[#020209] pt-24 pb-16">
      {/* Background glows */}
      <motion.div
        className="absolute rounded-full blur-[120px] opacity-20 bg-brand-green w-96 h-96 top-1/4 left-1/4"
        animate={{ scale: [1, 1.15, 1] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute rounded-full blur-[100px] opacity-15 bg-brand-blue w-80 h-80 bottom-1/3 right-1/4"
        animate={{ scale: [1, 1.12, 1] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 3 }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
        {/* Badge */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="inline-flex items-center px-4 py-2 rounded-full border border-brand-green/40 bg-brand-green/10 text-brand-green text-sm font-medium mb-8"
        >
          {t("agent.hero.badge")}
        </motion.div>

        {/* Title */}
        <motion.h1
          className="font-extrabold tracking-[-0.03em] leading-[1.05] mb-6"
          style={{ fontSize: 'clamp(40px, 6vw, 72px)' }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <span className="block text-white">{t("agent.hero.title.1")}</span>
          <span className="block bg-gradient-to-r from-brand-green via-brand-blue to-brand-gold bg-clip-text text-transparent">{t("agent.hero.title.2")}</span>
        </motion.h1>

        {/* Subtitle */}
        <motion.p
          className="text-neutral-400 text-lg md:text-xl max-w-3xl mx-auto leading-relaxed mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          {t("agent.hero.subtitle")}
        </motion.p>

        {/* Dashboard Image (Agent Textuel) */}
        <motion.div
          className="relative w-full max-w-5xl mx-auto mb-12"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          <div className="text-center mb-4">
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-brand-blue/30 bg-brand-blue/10 text-brand-blue text-sm font-medium">
              {lang === 'fr' ? 'Agent IA Textuel' : 'Text AI Agent'}
            </span>
          </div>
          <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-brand-green/10 border border-white/[0.05]">
            <img 
              src={DASHBOARD_IMAGE} 
              alt="Dashboard AIpreneur - Interface de gestion et chatbot" 
              className="w-full h-auto"
              data-testid="dashboard-image"
            />
            {/* Overlay gradient for blending */}
            <div className="absolute inset-0 bg-gradient-to-t from-[#020209]/40 via-transparent to-transparent pointer-events-none" />
          </div>
          
          {/* Floating badges */}
          <motion.div 
            className="absolute -left-4 top-1/4 bg-[#0A0A0F] border border-brand-green/30 rounded-xl px-4 py-3 shadow-lg hidden lg:block"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.8, duration: 0.5 }}
          >
            <div className="text-xs text-neutral-500 mb-1">Conversion Rate</div>
            <div className="text-xl font-bold text-brand-green">85.2%</div>
          </motion.div>
          
          <motion.div 
            className="absolute -right-4 top-1/3 bg-[#0A0A0F] border border-brand-gold/30 rounded-xl px-4 py-3 shadow-lg hidden lg:block"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 1, duration: 0.5 }}
          >
            <div className="text-xs text-neutral-500 mb-1">Uptime</div>
            <div className="text-xl font-bold text-brand-gold">99.9%</div>
          </motion.div>
        </motion.div>

        {/* Voice Agent Image */}
        <motion.div
          className="relative w-full max-w-4xl mx-auto"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
        >
          <div className="text-center mb-4">
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-brand-gold/30 bg-brand-gold/10 text-brand-gold text-sm font-medium">
              {lang === 'fr' ? 'Agent IA Vocal' : 'Voice AI Agent'}
            </span>
          </div>
          <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-brand-gold/10 border border-white/[0.05]">
            <img 
              src={VOICE_AGENT_IMAGE} 
              alt={lang === 'fr' ? "Agent IA Vocal - Interface de communication vocale" : "Voice AI Agent - Voice communication interface"}
              className="w-full h-auto"
              data-testid="voice-agent-image"
            />
            {/* Overlay gradient for blending */}
            <div className="absolute inset-0 bg-gradient-to-t from-[#020209]/40 via-transparent to-transparent pointer-events-none" />
          </div>
          
          {/* Voice badge */}
          <motion.div 
            className="absolute -right-4 bottom-1/4 bg-[#0A0A0F] border border-brand-gold/30 rounded-xl px-4 py-3 shadow-lg hidden lg:block"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 1.2, duration: 0.5 }}
          >
            <div className="text-xs text-neutral-500 mb-1">{lang === 'fr' ? 'Disponibilité' : 'Availability'}</div>
            <div className="text-xl font-bold text-brand-gold">24/7</div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
